import React, { useState } from "react";
import { Portal, Box } from "@mui/material";
import { SEVERITY_VARIANTS, BRANCH_NAMES, ASSISTANT_ENVIRONMENT } from "@consts";
import useSnackbar from "@hyro-components/hooks/useSnackbar";
import HyroFeedbackMessage from "@hyro-components/HyroFeedbackMessage";
import { useMutateSharePreview, useExternalPreviewLink } from "src/queries/hooks/preview";
import { selectedAccountIdSelector, assistantIdSelector, userDataSelector, systemModeSelector, hasPermissionSelector, selectedAssistantSelector } from "src/stores/selectors/appSelectors";
import useAppStore from "src/stores/appStore";
import { SNIPPET_Z_INDEX } from "src/components/widgets/overview/Snippet";
import { SYSTEM_MODES } from "src/stores/slices/createAppSlice";
import { datadogRum } from "@datadog/browser-rum";
import { trackEvent, ANALYTICS_EVENTS } from "@analytics";
import { USER_PERMISSIONS } from "@hyro/dashboard-permissions";
import { useDraft } from "src/queries/hooks/assistants";
import ShareDialog from "@hyro-components/Dialogs/ShareDialog";
import ExtendPreviewBanner from "./extend-preview/ExtendPreviewBanner";
import useExtendPreview from "./extend-preview/ExtendPreview.hook";

const SharePreviewDialog = ({ isSharePreviewDialogOpen, setIsSharePreviewDialogOpen }) => {
  const accountId = useAppStore(selectedAccountIdSelector);
  const assistantId = useAppStore(assistantIdSelector);
  const systemMode = useAppStore(systemModeSelector);
  const userData = useAppStore(userDataSelector);
  const selectedAssistant = useAppStore(selectedAssistantSelector);
  const { data: draft } = useDraft();
  const hasPermission = useAppStore(hasPermissionSelector);
  const isCopyLinkAllowed = hasPermission(USER_PERMISSIONS.APPS_TEAM);
  const sharePreviewMutation = useMutateSharePreview();
  const isLiveAgent = selectedAssistant?.environment === ASSISTANT_ENVIRONMENT.PRODUCTION;
  const branchName = systemMode === SYSTEM_MODES.EDIT ? draft?.branchName : isLiveAgent ? undefined : BRANCH_NAMES.MASTER;

  const shouldGetLink = isCopyLinkAllowed && isSharePreviewDialogOpen;
  const { data: previewLink } = useExternalPreviewLink(shouldGetLink ? { accountId, assistantId, branchName } : {});
  const [
    isSnackbarOpen,
    snackbarMessage,
    addSnackbarMessage,
    exitSnackbar,
    closeSnackbar
  ] = useSnackbar();

  const [snackbarSeverity, setSnackbarSeverity] = useState(SEVERITY_VARIANTS.SUCCESS);
  const [loading, setLoading] = useState(false);
  const { isExtendingPreview, handleExtendPreview, getExpirationText } = useExtendPreview(addSnackbarMessage, setSnackbarSeverity);

  const handleShare = async (emails) => {
    trackEvent(ANALYTICS_EVENTS.GENERAL.PREVIEW_SHARED, { assistantId, accountId, numOfRecipients: emails.length });

    setLoading(true);

    const params = { accountId, assistantId };
    const payload = { emails, userName: userData?.name, branchName };

    try {
      await sharePreviewMutation.mutateAsync({ params, payload });

      addSnackbarMessage("AI agent shared successfully");
      setSnackbarSeverity(SEVERITY_VARIANTS.SUCCESS);
    } catch (error) {
      addSnackbarMessage("Action failed. Please contact support.");
      setSnackbarSeverity(SEVERITY_VARIANTS.ERROR);
      return;
    } finally {
      setIsSharePreviewDialogOpen(false);
      setLoading(false);
    }
  };

  return <>
    <ShareDialog
      open={isSharePreviewDialogOpen}
      submitButtonLoading={loading}
      showCopyButton={isCopyLinkAllowed}
      title="Share your Hyro AI Agent"
      description={<span>Share your agent with others by entering their email below.</span >}
      copyText={previewLink}
      submitButtonText="Share"
      handleCancel={() => setIsSharePreviewDialogOpen(false)}
      handleSubmit={handleShare}
    >
      <Box sx={{ mt: 1 }}>
        <ExtendPreviewBanner getExpirationText={getExpirationText} handleExtendPreview={handleExtendPreview} isExtendingPreview={isExtendingPreview} />
      </Box>
    </ShareDialog>
    <Portal>
      <HyroFeedbackMessage
        sx={{ zIndex: SNIPPET_Z_INDEX + 1 }}
        message={snackbarMessage}
        severity={snackbarSeverity}
        showFeedback={isSnackbarOpen}
        onExited={exitSnackbar}
        closeFeedback={closeSnackbar}
      />
    </Portal>
  </>;
};

export default SharePreviewDialog;
